import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import type { ReactNode } from "react";
import { Icon } from "../../icons";
import { localized } from "../../localization";

/** The attributes an `imageBlock` node carries in the document JSON. */
export interface ImageBlockAttrs {
	fileId: string | null;
}

/**
 * Renders the picture for one file id. The editor package owns no API client, so the app
 * supplies this — a component that resolves the id to a signed URL and knows the file's
 * dimensions. Without one the block renders as a labelled placeholder, which is what every call
 * site that never embeds images gets for free.
 */
export type ImageRenderer = (attrs: { fileId: string; isEditable?: boolean }) => ReactNode;

/** Props Interface */
interface ImageBlockItemProps extends NodeViewProps {
	imageRenderer?: ImageRenderer;
}

export function ImageBlockItem({ node, editor, imageRenderer }: ImageBlockItemProps) {
	/** Computed */
	const { fileId } = node.attrs as ImageBlockAttrs;

	/** Render */
	return (
		<NodeViewWrapper
			data-cy="image-block"
			className="image-block my-2"
		>
			{fileId && imageRenderer ? (
				imageRenderer({ fileId, isEditable: editor.isEditable })
			) : (
				<div className="card flex items-center gap-2 p-4 text-mauve-11">
					<Icon name="Image" />
					<span>{localized("images.image")}</span>
				</div>
			)}
		</NodeViewWrapper>
	);
}
